import {
  useCallback, useContext, useMemo, useState,
} from 'react';
import format from 'string-template';

import InternalAssignment from '../../../../Model/InternalAssignment';
import CoachConfig from '../../../Model/CoachConfig';
import LoggedInUserContext from '../../../../context/LoggedInUserContext';
import CoachesListContext from '../../../context/CoachesListContext';
import useComponentMounted from '../../../../hooks/useComponentMounted';
import useToast from '../../../hooks/useToast';
import { PoolMembershipAction, capturePoolMembershipChange } from '../../../utils/poolTelemetry';
import { isCoachPoolEligible } from '../utils/poolAssignments';

import texts from '../texts';

const useMoveToPool = () => {
  const { userId: loggedInUserId, userDoc } = useContext(LoggedInUserContext);
  const { coachesCollection } = useContext(CoachesListContext);
  const [transitionsToConfirm, setTransitionsToConfirm] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const { showToast } = useToast();
  const isComponentMountedRef = useComponentMounted();

  /**
   * Announce what a pool move actually committed, and report which coaches it changed. Creating the
   * `POOL` row and disabling the coach's `AC` rows are both changes, so a coach who was already
   * pooled but held AC assignments is a change too. Telemetry fires only for created memberships,
   * since that is what pool membership changing means.
   * @param {{pooledCoachIds: Array<string>, unassignedFromAcCoachIds: Array<string>}} changes - The
   * changes that committed.
   * @param {number} attemptedCount - How many coaches the move was attempted for. When fewer changed
   * than were attempted because the run failed part-way, the toast has to say so — a plain success
   * next to the error toast reads as a contradiction and hides how much actually landed.
   * @return {Set<string>} The coach ids something changed for.
   */
  const reportPoolChanges = useCallback(({ pooledCoachIds, unassignedFromAcCoachIds }, attemptedCount = null) => {
    pooledCoachIds.forEach((id) => capturePoolMembershipChange(loggedInUserId, id, PoolMembershipAction.ADDED));

    const changedIds = new Set([...pooledCoachIds, ...unassignedFromAcCoachIds]);
    if (!changedIds.size) {
      return changedIds;
    }

    if (attemptedCount === null) {
      showToast(texts.coachesAddedToPool, { success: true });
    } else {
      showToast(
        format(texts.partialMoveToPool, { count: changedIds.size, total: attemptedCount }),
        { warning: true },
      );
    }

    return changedIds;
  }, [loggedInUserId, showToast]);

  const moveCoachesToPool = useCallback(async (transitions) => {
    setIsSaving(true);
    try {
      const changes = await InternalAssignment.addCoachesToPool(
        transitions.map(({ id }) => id),
        loggedInUserId,
        userDoc?.name,
      );

      const changedIds = reportPoolChanges(changes);
      /*
        Only reachable on a fully successful move: every coach that is not in changedIds was already
        pooled with no AC assignment, so nothing was written for them. On a failure the untouched
        coaches were not skipped, they were never attempted, which the error toast covers instead.
      */
      const skippedNames = transitions.filter(({ id }) => !changedIds.has(id)).map(({ name }) => name);
      if (skippedNames.length) {
        showToast(format(texts.alreadyPooled, { coaches: skippedNames.join(', ') }), { warning: true });
      }
    } catch (error) {
      // A failure part-way through a multi-batch move leaves the earlier batches durable.
      if (error.committedPoolChanges) {
        reportPoolChanges(error.committedPoolChanges, transitions.length);
      }
      showToast(format(texts.addToPoolFailed, { error: error.message }), { error: true });
    } finally {
      if (isComponentMountedRef.current) {
        setIsSaving(false);
        setTransitionsToConfirm([]);
      }
    }
  }, [loggedInUserId, userDoc, showToast, isComponentMountedRef, reportPoolChanges]);

  const showPodsBlockedToast = useCallback((coachNames) => {
    showToast(format(texts.podsBlocked, { coaches: coachNames.join(', ') }), { error: true });
  }, [showToast]);

  /*
    Every way into the pool comes through here (the page's Move to Pool, the roster's add, and removing a
    coach's last AC), so this is where coaches without Assistant Coaching are kept out: the pool is served by
    the AC organization, and turning AC off takes a coach out of it again. The flag is read off the coach
    list rather than the transition, because callers only hold a coach's id and name.
  */
  const startMoveToPool = useCallback(async (requestedTransitions, { onBlockedByPods = showPodsBlockedToast } = {}) => {
    const coaches = coachesCollection.docs.slice();
    const transitions = [];
    const acDisabledNames = [];
    requestedTransitions.forEach((transition) => {
      if (isCoachPoolEligible(coaches, transition.id)) {
        transitions.push(transition);
      } else {
        acDisabledNames.push(transition.name);
      }
    });

    if (acDisabledNames.length) {
      showToast(format(texts.acNotEnabledBlocked, { coaches: acDisabledNames.join(', ') }), { error: true });
    }

    if (!transitions.length) {
      return;
    }

    setIsSaving(true);
    let coachConfigs;
    try {
      coachConfigs = await Promise.all(transitions.map(({ id }) => CoachConfig.getCoachConfigByCoachId(id)));
    } catch (error) {
      showToast(format(texts.podCheckFailed, { error: error.message }), { error: true });
      if (isComponentMountedRef.current) {
        setIsSaving(false);
      }
      return;
    }

    if (!isComponentMountedRef.current) {
      return;
    }

    const podEnabledNames = transitions
      .filter((_, index) => coachConfigs[index]?.isPodEnabled)
      .map(({ name }) => name);
    const eligibleTransitions = transitions.filter((_, index) => !coachConfigs[index]?.isPodEnabled);

    if (podEnabledNames.length) {
      onBlockedByPods(podEnabledNames);
    }

    if (!eligibleTransitions.length) {
      setIsSaving(false);
      return;
    }

    if (eligibleTransitions.some(({ currentAcNames }) => currentAcNames.length)) {
      setIsSaving(false);
      setTransitionsToConfirm(eligibleTransitions);
      return;
    }

    await moveCoachesToPool(eligibleTransitions);
  }, [coachesCollection, isComponentMountedRef, showToast, showPodsBlockedToast, moveCoachesToPool]);

  const confirmMoveToPool = useCallback(
    () => moveCoachesToPool(transitionsToConfirm),
    [moveCoachesToPool, transitionsToConfirm],
  );

  const cancelMoveToPool = useCallback(() => setTransitionsToConfirm([]), []);

  return useMemo(() => ({
    startMoveToPool,
    confirmMoveToPool,
    cancelMoveToPool,
    transitionsToConfirm,
    isSaving,
  }), [
    startMoveToPool,
    confirmMoveToPool,
    cancelMoveToPool,
    transitionsToConfirm,
    isSaving,
  ]);
};

export default useMoveToPool;
